'use client';

import { EventType, InteractionType } from '@azure/msal-browser';
import { MsalAuthenticationTemplate, MsalProvider } from '@azure/msal-react';
import * as React from 'react';
import { createContext, Dispatch, useEffect, useMemo, useReducer } from 'react';
import { application } from './application';
import { ErrorComponent } from './components/ErrorComponent';
import { LoadingComponent } from './components/LoadingComponent';
import { authenticationRequest } from './config';

const AuthAction = {
  SET_FIRST_NAME: 'SET_FIRST_NAME',
} as const;
type AuthAction = (typeof AuthAction)[keyof typeof AuthAction];

type AuthState = {
  firstName: string;
};
type AuthDispatchAction = { type: AuthAction; payload: unknown };

const initialState: AuthState = {
  firstName: '',
};

export const authReducer = (
  state: AuthState,
  action: AuthDispatchAction
): AuthState => {
  switch (action.type) {
    case AuthAction.SET_FIRST_NAME:
      return { ...state, firstName: action.payload as string };
    default:
      return state;
  }
};

type AuthContextValue = {
  state: AuthState;
  dispatch: Dispatch<AuthDispatchAction>;
};
export const AuthContext = createContext<AuthContextValue>({
  state: initialState,
  dispatch: () => {},
});

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export const AuthProvider = (props: React.PropsWithChildren<{}>) => {
  const [isReady, setIsReady] = React.useState(false);
  const [state, dispatch] = useReducer(authReducer, initialState);
  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state]
  );
  useEffect(() => {
    let cancelled = false;

    (async () => {
      await application.initialize();
      await application.handleRedirectPromise();

      if (
        !application.getActiveAccount() &&
        application.getAllAccounts().length > 0
      ) {
        application.setActiveAccount(application.getAllAccounts()[0]);
      }

      application.addEventCallback(event => {
        if (
          event.eventType === EventType.LOGIN_SUCCESS &&
          event.payload &&
          'account' in event.payload &&
          event.payload.account
        ) {
          application.setActiveAccount(event.payload.account);
        }
      });

      if (!cancelled) setIsReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!isReady) return <LoadingComponent />;

  return (
    <AuthContext.Provider value={contextValue}>
      <MsalProvider instance={application}>
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          authenticationRequest={authenticationRequest}
          errorComponent={ErrorComponent}
          loadingComponent={LoadingComponent}
        >
          {props.children}
        </MsalAuthenticationTemplate>
      </MsalProvider>
    </AuthContext.Provider>
  );
};
